
import React, { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase'; // Adjust the import path as needed
import './TranslatorPage.css';

interface Language {
  code: string;
  name: string;
}

const TranslatorPage: React.FC = () => {
  const [inputText, setInputText] = useState<string>('');
  const [translatedText, setTranslatedText] = useState<string>('');
  const [sourceLang, setSourceLang] = useState<string>('auto');
  const [targetLang, setTargetLang] = useState<string>('en');
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [availableLanguages, setAvailableLanguages] = useState<Language[]>([]);
  const [languagesLoading, setLanguagesLoading] = useState<boolean>(true);
  const [languagesError, setLanguagesError] = useState<string | null>(null);

  useEffect(() => {
    const fetchLanguages = async () => {
      try {
        const getLanguages = httpsCallable(functions, 'getLanguages');
        const result = await getLanguages();
        const data = result.data as { languages: Language[] };
        setAvailableLanguages(data.languages);
      } catch (err: any) {
        setLanguagesError(`Failed to load languages: ${err.message}`);
        console.error('Error fetching languages:', err);
      } finally {
        setLanguagesLoading(false);
      }
    };
    fetchLanguages();
  }, []);

  const handleTextTranslate = async () => {
    if (!inputText.trim()) {
      setError('Please enter text to translate.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const translateText = httpsCallable(functions, 'translateText');
      const result = await translateText({ text: inputText, sourceLang, targetLang });
      const data = result.data as { translatedText: string };
      setTranslatedText(data.translatedText);
    } catch (err: any) {
      setError(`Translation failed: ${err.message}`);
      console.error('Translation error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setFile(event.target.files[0]);
    }
  };

  const handleFileTranslate = async () => {
    if (!file) {
      setError('Please select a file to translate.');
      return;
    }
    setLoading(true);
    setError(null);

    const reader = new FileReader();
    reader.onload = async (e) => {
      const fileContent = e.target?.result as string;
      try {
        const translateFile = httpsCallable(functions, 'translateFile');
        const result = await translateFile({
          filename: file.name,
          content: fileContent,
          sourceLang,
          targetLang,
        });
        const data = result.data as { translatedContent: string };
        setTranslatedText(`File translated. Content: ${data.translatedContent.substring(0, 500)}...`);
      } catch (err: any) {
        setError(`File translation failed: ${err.message}`);
        console.error('File translation error:', err);
      } finally {
        setLoading(false);
      }
    };
    reader.readAsText(file);
  };

  return (
    <div className="translator-page-container">
      <h1>Text and File Translator</h1>

      <div className="translator-section">
        <h2>Text Translation</h2>
        <textarea
          placeholder="Enter text to translate..."
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          rows={6}
        ></textarea>
        <div className="language-selectors">
          <label>
            Source Language:
            <select value={sourceLang} onChange={(e) => setSourceLang(e.target.value)} disabled={languagesLoading}>
              <option value="auto">Detect Language</option>
              {languagesLoading ? (
                <option>Loading languages...</option>
              ) : languagesError ? (
                <option>Error loading languages</option>
              ) : (
                availableLanguages.map((lang) => (
                  <option key={lang.code} value={lang.code}>
                    {lang.name}
                  </option>
                ))
              )}
            </select>
          </label>
          <label>
            Target Language:
            <select value={targetLang} onChange={(e) => setTargetLang(e.target.value)} disabled={languagesLoading}>
              {languagesLoading ? (
                <option>Loading languages...</option>
              ) : languagesError ? (
                <option>Error loading languages</option>
              ) : (
                availableLanguages.map((lang) => (
                  <option key={lang.code} value={lang.code}>
                    {lang.name}
                  </option>
                ))
              )}
            </select>
          </label>
        </div>
        <button onClick={handleTextTranslate} disabled={loading}>
          {loading ? 'Translating...' : 'Translate Text'}
        </button>
      </div>

      <div className="translator-section">
        <h2>File Translation</h2>
        <input type="file" onChange={handleFileChange} />
        <button onClick={handleFileTranslate} disabled={loading || !file}>
          {loading ? 'Translating File...' : 'Translate File'}
        </button>
      </div>

      {error && <p className="error-message">{error}</p>}
      {languagesError && <p className="error-message">{languagesError}</p>}

      {translatedText && (
        <div className="translated-output">
          <h2>Translated Output:</h2>
          <p>{translatedText}</p>
        </div>
      )}
    </div>
  );
};

export default TranslatorPage;
